import { css } from '@emotion/css';
import { Group, Tabs, useMantineTheme } from '@mantine/core';
import { useTranslation } from 'locales';
import { useEffect } from 'react';
import { useParams } from 'react-router';
import { isEmpty, isNullish, prop } from 'remeda';
import { useLoadTemplateV2 } from 'shared/hooks/use-load-template-v2';
import { AddChannelButton } from './AddChannelButton';
import { AttachmentsSelector } from './AttachmentsSelector';
import { CHANNEL_CONFIGS, type TemplateChannel } from './channels';
import { StatementTypeSelector } from './StatementTypeSelector';
import { useActiveChannelAtom } from './state';

export const TemplateEditorTabs = () => {
  const { t } = useTranslation();
  const { templateId } = useParams<{ templateId: string }>();
  const { template } = useLoadTemplateV2(templateId);
  const [activeChannel, setActiveChannel] = useActiveChannelAtom();
  const theme = useMantineTheme();

  const availableChannels = isNullish(template)
    ? []
    : CHANNEL_CONFIGS.filter(({ key }) => !isEmpty(template.bodies[key]));
  const channelsSignature = availableChannels.map(prop('key')).join(',');

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset to the first tab whenever the set of populated channels changes; the first channel is read fresh
  useEffect(() => {
    setActiveChannel(availableChannels[0]?.key ?? null);
  }, [channelsSignature]);

  if (isNullish(template)) return null;

  // Channel deletion is disabled: templates are always created with every channel populated.
  // const openDeleteChannelModal = (channel: TemplateChannel, label: string) =>
  //   modals.open({
  //     title: t(i18nKeys.TEMPLATE.DELETE_CHANNEL_BODIES_TITLE, { channel: label }),
  //     centered: true,
  //     children: (
  //       <DeleteChannelBodiesModal
  //         templateId={template.id}
  //         bodyIds={template.bodies[channel].map(prop('id'))}
  //         channelLabel={label}
  //         onClose={modals.closeAll}
  //       />
  //     ),
  //   });

  return (
    <Tabs
      value={activeChannel}
      onChange={value => setActiveChannel(value as TemplateChannel | null)}
      classNames={{ tab: tabClass(theme) }}
    >
      <Tabs.List>
        {availableChannels.map(({ key, labelKey, Icon }) => (
          <Tabs.Tab
            key={key}
            value={key}
            leftSection={<Icon size={20} stroke={key === activeChannel ? 2 : 1.5} />}
            // rightSection={
            //   <ChannelTabCloseButton
            //     label={t(i18nKeys.TEMPLATE.DELETE_CHANNEL_BODIES_TITLE, { channel: t(labelKey) })}
            //     reason={template.noChannelDeletionReasons[key]}
            //     onDelete={() => openDeleteChannelModal(key, t(labelKey))}
            //   />
            // }
          >
            {t(labelKey)}
          </Tabs.Tab>
        ))}
        <AddChannelButton template={template} />
        <Group ml="auto" pr="sm" align="center">
          <StatementTypeSelector template={template} />
          <AttachmentsSelector template={template} />
        </Group>
      </Tabs.List>
    </Tabs>
  );
};

// type ChannelTabCloseButtonProps = {
//   label: string;
//   reason: string | null;
//   onDelete: () => void;
// };
//
// const ChannelTabCloseButton = ({ label, reason, onDelete }: ChannelTabCloseButtonProps) => (
//   <Tooltip label={reason} disabled={isNullish(reason)} multiline maw={320} position="top">
//     <Box>
//       <ActionIcon
//         component="div"
//         variant="subtle"
//         color="gray"
//         size="sm"
//         disabled={isNonNullish(reason)}
//         aria-label={label}
//         onClick={event => {
//           event.stopPropagation();
//           if (isNullish(reason)) onDelete();
//         }}
//       >
//         <IconX size={16} stroke={2} />
//       </ActionIcon>
//     </Box>
//   </Tooltip>
// );

const tabClass = (theme: ReturnType<typeof useMantineTheme>) => css`
  font-size: ${theme.fontSizes.sm};
  padding-top: 14px;
  padding-bottom: 14px;
  padding-left: 14px;
  padding-right: 14px;
  border-radius: 0px;
  font-weight: 500;

  &:hover:not([data-active]) {
    background-color: ${theme.colors.purple[0]};
    border-color: ${theme.colors.purple[2]};
    color: ${theme.colors.purple[7]};
  }

  &[data-active] {
    font-weight: 700;
    color: ${theme.colors.purple[6]};
    border-color: ${theme.colors.purple[6]};
  }
`;
